import { LinkOutlined } from '@ant-design/icons'
import { Button, Card, Form, Input, Select, Table, TablePaginationConfig, TableProps } from 'antd'
import { TransactionData } from 'Modules'
import { useCallback, useEffect, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import ButtonExport from 'component/button/button-export'
import LinkAddress from 'component/link/link-address'
import LinkAddressCopy from 'component/link/link-address-copy'
import { HISTORY_TYPE, HISTORY_TYPE_ARRAY } from 'constants/history-type'
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE } from 'constants/pagination'
import { usePTOContext } from 'context/pto-context-provider'
import { convertDateToString } from 'utils/format-time'
import { logError } from 'utils/logger'

const Transaction = () => {
  const { contractAddress } = usePTOContext()
  const [loading, setLoading] = useState(false)
  const [addressSearch, setAddressSearch] = useState('')
  const [dataTable, setDataTable] = useState<TransactionData[]>([])
  const [total, setTotal] = useState(0)
  const [type, setType] = useState<number | null>(null)
  const [pageIndex, setPageIndex] = useState(DEFAULT_PAGE_INDEX)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)

  /**
   * handle change pagination
   * @param pagination
   */
  const handleTableChange = (pagination: TablePaginationConfig) => {
    const { current: newPage, pageSize: newPageSize } = pagination
    setPageIndex(newPage || DEFAULT_PAGE_INDEX)
    setPageSize(newPageSize || DEFAULT_PAGE_SIZE)
  }

  /**
   * func get data table
   * @param pageIndex
   * @param pageSize
   * @returns
   */
  const getDataTable = useCallback(
    async (pageIndex = DEFAULT_PAGE_INDEX, pageSize = DEFAULT_PAGE_SIZE) => {
      setLoading(true)
      try {
        if (!contractAddress) return
        const res = await AppAdmin.getListPTOTransaction({
          contractAddress,
          limit: pageSize,
          page: pageIndex,
          ownerAddress: addressSearch,
          type,
        })

        if (res?.data.success) {
          const { results, totalResults } = res?.data.data || {}
          const transaction = results?.map((item: any, index: number) => {
            const objectType = HISTORY_TYPE_ARRAY.find((c) => c.value === item.byType)
            return {
              key: `${pageIndex}-${index}`,
              date: convertDateToString(item.createdAt),
              type: objectType?.label || '',
              address: item.ownerAddress,
              contract: item.contractAddress,
              amount: item.amount,
              asset: item.assetToken,
              txHash: item.txHash,
              status: item.byStatus,
            }
          })
          setDataTable(transaction)
          setTotal(totalResults || 0)
        }
      } catch (error) {
        setDataTable([])
        setTotal(0)
        logError('Error fetching data: ', error)
      } finally {
        //setLoading(false);
      }
      setLoading(false)
    },
    [addressSearch, contractAddress, type]
  )

  /**
   * handle on click button search
   */
  const handleSearch = () => {
    setPageIndex(DEFAULT_PAGE_INDEX)
    setPageSize(DEFAULT_PAGE_SIZE)
    getDataTable(DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE)
  }

  const statusClassMap: any = {
    1: { text: 'Pending', className: 'status-pending' },
    2: { text: 'Success', className: 'status-success' },
    3: { text: 'Fail', className: 'status-fail' },
  }

  const columns: TableProps<TransactionData>['columns'] = [
    {
      title: 'Address',
      dataIndex: 'address',
      key: 'address',
      render: (text) => <LinkAddressCopy address={text} />,
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
    },
    {
      title: 'Asset',
      dataIndex: 'asset',
      key: 'asset',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const { text, className } = statusClassMap[status] || {}
        return <span className={className}>{text || 'Unknown'}</span>
      },
    },
    {
      title: 'Tx Hash',
      dataIndex: 'txHash',
      key: 'txHash',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <LinkAddress address={text} type="tx" icon={<LinkOutlined />} />
        </div>
      ),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
    },
  ]

  useEffect(() => {
    if (contractAddress) {
      getDataTable(pageIndex, pageSize)
    }
  }, [pageIndex, pageSize, addressSearch, type, contractAddress, getDataTable])

  return (
    <>
      <Card className="formSearch">
        <Form layout="inline" style={{ padding: 20, justifyContent: 'start' }}>
          <Form.Item>
            <Input
              allowClear
              type="text"
              placeholder="By address"
              value={addressSearch}
              onChange={(e) => {
                setAddressSearch(e.target.value)
              }}
            />
          </Form.Item>
          <Form.Item>
            <Select
              allowClear
              placeholder="By type"
              options={[
                { value: null, label: 'All' },
                { value: HISTORY_TYPE.TOKENIZE_DEPOSIT, label: 'Purchased' },
                { value: HISTORY_TYPE.TOKENIZE_WITHDRAW, label: 'Claim' },
              ]}
              value={type}
              onChange={(value) => {
                setPageIndex(DEFAULT_PAGE_INDEX)
                setType(value)
              }}
              style={{ width: '200px', textAlign: 'start' }}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={handleSearch}>
              Search
            </Button>
          </Form.Item>
          <Form.Item>
            <ButtonExport
              fileType="pto_transactions"
              disabled={!contractAddress}
              onExport={() =>
                AppAdmin.exportPTOTransaction({
                  contractAddress: contractAddress as string,
                  ownerAddress: addressSearch,
                  type,
                })
              }
            />
          </Form.Item>
        </Form>
      </Card>
      <div className="p2p-detail-table">
        <Card style={{ margin: '0 auto' }}>
          <Table<TransactionData>
            columns={columns}
            dataSource={dataTable}
            loading={loading}
            pagination={total < 11 ? false : { total, current: pageIndex, pageSize }}
            onChange={handleTableChange}
            scroll={{ x: 1000 }}
            bordered
          />
        </Card>
      </div>
    </>
  )
}

export default Transaction
